import { useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import type { AdditionalInputField } from '@/types/checkout';
import { AdyenPayFormUI } from './AdyenPayFormUI';
import { type AdyenPaymentResponse } from '@/types/payment';
import { ProviderEnum, type PaymentMethodEnum } from '@/types/common';
import { useTranslate } from '@/hooks/useTranslate';
import { isEmailValid } from '@/utils/email';
import { AdyenInputType } from '@/types/adyen';
import type { Address } from '@/api/address';
import { shouldShowAddressFieldForTaxCalculation } from '@/utils/tax';
import { getDefaultFieldValues } from '@/utils/adyen';

interface Props {
  selectedPaymentMethod: PaymentMethodEnum;
}

export const AdyenPayForm = ({ selectedPaymentMethod }: Props) => {
  const {
    paymentMethodsSettings,
    totalPrice,
    customer,
    featureFlags,
    actions: { runPayment, setIsFormValid },
  } = useContext(CheckoutContext);
  const { t } = useTranslate();
  const [isZipCodeValid, setIsZipCodeValid] = useState(false);
  const paymentMethod = useMemo(() => {
    return paymentMethodsSettings.find((method) => method.paymentMethodName === selectedPaymentMethod);
  }, [paymentMethodsSettings, selectedPaymentMethod]);

  const additionalInputFields = useMemo(() => {
    if (!paymentMethod) {
      return [];
    }
    const paymentMethodFields = paymentMethodsSettings.find(
      (method) => method.id === paymentMethod.id
    )?.additionalInputFields;
    return paymentMethodFields;
  }, [paymentMethodsSettings, paymentMethod]);

  const [fieldValues, setFieldValues] = useState<Record<string, string | Address>>(() =>
    getDefaultFieldValues(additionalInputFields ?? [], customer, featureFlags)
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [billingAddress, setBillingAddress] = useState<Address | null>(
    () => (fieldValues[AdyenInputType.BILLING_ADDRESS] as Address | undefined) ?? null
  );

  const [fieldTouched, setFieldTouched] = useState<Record<string, boolean>>(
    additionalInputFields?.reduce(
      (acc, field) => {
        acc[field.apm_key] = false;
        return acc;
      },
      {} as Record<string, boolean>
    ) ?? {}
  );

  const validateField = useCallback(
    (field: AdditionalInputField, value: string): string => {
      if (!value) {
        return t('fieldErrors.incomplete', { field: field.display_name });
      }

      if (field.validation) {
        try {
          const regex = new RegExp(field.validation, 'u');
          if (!regex.test(value)) {
            return t('fieldErrors.invalidFormat', { field: field.display_name.toLowerCase() });
          }
        } catch (error) {
          console.error(`Invalid regex pattern for ${field.apm_key}:`, error);
        }
      }

      return '';
    },
    [t]
  );

  const handlePayment = useCallback(async () => {
    await runPayment<AdyenPaymentResponse>(fieldValues, undefined, ProviderEnum.ADYEN);
  }, [fieldValues, runPayment]);

  const handleFieldChange = useCallback(
    (field: AdditionalInputField, value: string) => {
      setFieldValues((prev) => ({
        ...prev,
        [field.apm_key]: value,
      }));
      const error = validateField(field, value);

      if (error) {
        setFieldErrors((prev) => ({
          ...prev,
          [field.apm_key]: error,
        }));
      } else {
        setFieldErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field.apm_key];
          return newErrors;
        });
      }
    },
    [validateField]
  );

  const handleAddressSelect = useCallback((field: AdditionalInputField, address: Address) => {
    setBillingAddress(address);
    setFieldValues((prev) => ({
      ...prev,
      [field.apm_key]: address,
    }));
    setFieldErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field.apm_key];
      return newErrors;
    });
  }, []);

  const handleFieldBlur = useCallback((field: AdditionalInputField) => {
    setFieldTouched((prev) => ({
      ...prev,
      [field.apm_key]: true,
    }));
  }, []);

  // When the tax-flow Address component is active (US/CA + COLLECT_FULL_ADDRESS),
  // it already collects the billing address; suppress UnmanagedAddress and
  // route the tax Address selection into the BILLING_ADDRESS field instead.
  const billingAddressField = useMemo(
    () => additionalInputFields?.find((f) => f.apm_key === AdyenInputType.BILLING_ADDRESS),
    [additionalInputFields]
  );

  const useTaxAddressForBilling =
    !!billingAddressField && shouldShowAddressFieldForTaxCalculation(customer, featureFlags);

  const handleTaxAddressSelect = useCallback(
    (address: Address) => {
      if (billingAddressField) {
        handleAddressSelect(billingAddressField, address);
      }
    },
    [billingAddressField, handleAddressSelect]
  );

  // Update global form validity
  const isFormValid = useMemo(() => {
    const fieldsWithoutEmail = additionalInputFields?.filter(
      (field) => field.apm_key !== AdyenInputType.EMAIL && field.apm_key !== AdyenInputType.SHOPPER_EMAIL
    );
    if (
      !additionalInputFields ||
      additionalInputFields.length === 0 ||
      (isEmailValid(customer?.email) && fieldsWithoutEmail?.length === 0 && isZipCodeValid)
    ) {
      return true;
    }

    const allFieldsFilled = fieldsWithoutEmail?.every((field) => {
      if (field.apm_key === AdyenInputType.BILLING_ADDRESS) {
        return billingAddress !== null;
      }
      return (
        fieldValues[field.apm_key] &&
        typeof fieldValues[field.apm_key] === 'string' &&
        (fieldValues[field.apm_key] as string).trim() !== ''
      );
    });
    const noErrors = Object.keys(fieldErrors).length === 0;

    return Boolean(allFieldsFilled && noErrors && isZipCodeValid && isEmailValid(customer?.email));
  }, [fieldValues, fieldErrors, additionalInputFields, customer?.email, isZipCodeValid, billingAddress]);

  useEffect(() => {
    setIsFormValid(isFormValid);
  }, [isFormValid, setIsFormValid]);

  const handleTaxValidationChange = useCallback((isValid: boolean) => {
    setIsZipCodeValid(isValid);
  }, []);

  if (!additionalInputFields || additionalInputFields.length === 0) {
    return null;
  }

  return (
    <AdyenPayFormUI
      handleTaxValidationChange={handleTaxValidationChange}
      additionalInputFields={additionalInputFields}
      fieldValues={fieldValues}
      fieldErrors={fieldErrors}
      fieldTouched={fieldTouched}
      totalPrice={totalPrice}
      onFieldChange={handleFieldChange}
      onFieldBlur={handleFieldBlur}
      onAddressSelect={handleAddressSelect}
      onPayment={handlePayment}
      isDisabled={!isFormValid || !isEmailValid(customer?.email)}
      useTaxAddressForBilling={useTaxAddressForBilling}
      onTaxAddressSelect={useTaxAddressForBilling ? handleTaxAddressSelect : undefined}
    />
  );
};
